import * as Sentry from "@sentry/react";
import type {
  RawScannerEvent,
  RawScannerTarget,
  RawScannerTracePayload,
} from "./scannerDiagnostics";
import {
  hasBarcodeIdentifier,
  hasConsecutiveRapidKeydownCadence,
} from "./scannerTraceClassification";

export const SCANNER_SENTRY_MESSAGE = "barcode_scanner_diagnostic";
const SCANNER_EVENT_CHUNK_SIZE = 25;
const MINIMUM_RAPID_EVENT_GAPS = 5;
const MAXIMUM_SCANNER_EVENT_GAP_MS = 80;
let scannerSentrySequence = 0;

const getScannerDiagnosticId = () => {
  scannerSentrySequence += 1;
  return `scanner-${Date.now().toString(36)}-${scannerSentrySequence}`;
};

const flattenTarget = (prefix: string, target: RawScannerTarget) => ({
  [`${prefix}Tag`]: target.tag,
  [`${prefix}Id`]: target.id,
  [`${prefix}Name`]: target.name,
  [`${prefix}Type`]: target.type,
  [`${prefix}Placeholder`]: target.placeholder,
  [`${prefix}BarcodeInput`]: target.barcodeInput,
  [`${prefix}ContentEditable`]: target.contentEditable,
  [`${prefix}ValueLength`]: target.valueLength,
  [`${prefix}Value`]: target.value,
});

const flattenScannerEvent = (event: RawScannerEvent) => {
  const { target, activeElement, resolution, ...eventFields } = event;
  return {
    ...eventFields,
    resolutionStatus: resolution?.status || "",
    resolutionKey: resolution?.status === "resolved" ? resolution.key : "",
    resolutionSource: resolution?.source || "",
    ...flattenTarget("target", target),
    ...flattenTarget("activeElement", activeElement),
  };
};

const chunkEvents = (events: RawScannerEvent[]) => {
  if (events.length === 0) return [[]];
  return Array.from(
    { length: Math.ceil(events.length / SCANNER_EVENT_CHUNK_SIZE) },
    (_, index) =>
      events.slice(
        index * SCANNER_EVENT_CHUNK_SIZE,
        (index + 1) * SCANNER_EVENT_CHUNK_SIZE
      )
  );
};

const getScannerContext = (payload: RawScannerTracePayload) => ({
  route:
    typeof payload.details?.traceRoute === "string"
      ? payload.details.traceRoute
      : window.location.href,
  userAgent: navigator.userAgent,
  platform: navigator.platform,
  language: navigator.language,
  environment: process.env.REACT_APP_ENV || "development",
  release: process.env.REACT_APP_SENTRY_RELEASE || "",
});

const hasBarcodeTarget = (event: RawScannerEvent) =>
  [event.target, event.activeElement].some(
    (target) =>
      target.barcodeInput === "true" ||
      hasBarcodeIdentifier([
        target.barcodeInput,
        target.id,
        target.name,
        target.placeholder,
      ])
  );

const hasScannerCadence = (events: RawScannerEvent[]) =>
  hasConsecutiveRapidKeydownCadence(
    events,
    MINIMUM_RAPID_EVENT_GAPS,
    MAXIMUM_SCANNER_EVENT_GAP_MS
  );

const shouldCaptureScannerTrace = (payload: RawScannerTracePayload) =>
  payload.reason === "dispatch" ||
  payload.events.some(hasBarcodeTarget) ||
  hasScannerCadence(payload.events);

export const captureScannerTrace = (payload: RawScannerTracePayload) => {
  try {
    if (!shouldCaptureScannerTrace(payload)) return;
    const diagnosticId = getScannerDiagnosticId();
    const chunks = chunkEvents(payload.events);
    chunks.forEach((events, index) => {
      Sentry.captureMessage(SCANNER_SENTRY_MESSAGE, {
        level: payload.reason === "dispatch" ? "info" : "warning",
        fingerprint: [SCANNER_SENTRY_MESSAGE, payload.reason],
        tags: {
          feature: "barcode-scanner",
          scanner_owner: payload.ownerKey,
          scanner_reason: payload.reason,
          scanner_diagnostic_id: diagnosticId,
          scanner_chunk: `${index + 1}/${chunks.length}`,
        },
        extra: {
          diagnosticId,
          ownerKey: payload.ownerKey,
          reason: payload.reason,
          rawBuffer: payload.rawBuffer,
          normalizedCandidate: payload.normalizedCandidate || "",
          transformations: payload.transformations || [],
          eventCount: payload.events.length,
          details: payload.details || {},
          ...getScannerContext(payload),
          events: events.map(flattenScannerEvent),
        },
      });
    });
  } catch (error) {
    // Scanner handling must remain operational if observability is unavailable.
    // eslint-disable-next-line no-console
    console.warn("[BarcodeScannerRaw]:sentry-failed", error);
  }
};
